import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  PlusIcon,
  PencilSquareIcon,
  NoSymbolIcon,
  TrashIcon,
  HomeModernIcon,
  UserGroupIcon,
  ArrowPathIcon,
  ShieldCheckIcon,
  EnvelopeIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import PageHeader from "../../components/layout/PageHeader";
import { Table } from "../../components/ui/Table";
import { Badge } from "../../components/ui/Badge";
import { Avatar } from "../../components/ui/Avatar";
import { Modal } from "../../components/ui/Modal";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Select } from "../../components/ui/Select";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import FormerCamperLink from "../../components/forms/FormerCamperLink";
import OverrideCounselorCabinDialog from "../../components/forms/OverrideCounselorCabinDialog";
import { cabinsApi } from "../../api/cabins";
import { cabinGroupsApi } from "../../api/cabinGroups";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import toast from "react-hot-toast";
import { useAuth } from "../../hooks/useAuth";
import type { User, UserRole, Cabin, CabinGroup } from "../../types";
import api from "../../api/axios";

const createSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Valid email is required"),
  password: z.string().min(8, "Password must be at least 8 characters").optional().or(z.literal("")),
  role: z.enum(["admin", "director", "counselor", "readonly"]),
  cabinId: z.preprocess((v) => (v === "" ? undefined : v), z.coerce.number().optional()),
  sendInvite: z.boolean().optional(),
});

const editSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Valid email is required"),
  password: z.string().min(8, "Password must be at least 8 characters").optional().or(z.literal("")),
  role: z.enum(["admin", "director", "counselor", "readonly"]),
  cabinId: z.preprocess((v) => (v === "" ? undefined : v), z.coerce.number().optional()),
  excludedFromReports: z.boolean().optional(),
  notes: z.string().optional(),
});

const roleSchema = z.object({
  role: z.enum(["admin", "director", "counselor", "readonly"]),
  password: z.string().min(8, "Password must be at least 8 characters").optional().or(z.literal("")),
});

type CreateFormData = z.infer<typeof createSchema>;
type EditFormData = z.infer<typeof editSchema>;
type RoleFormData = z.infer<typeof roleSchema>;


const roleOptions = [
  { value: "admin", label: "Admin" },
  { value: "director", label: "Director" },
  { value: "counselor", label: "Counselor" },
  { value: "readonly", label: "Read Only" },
];

export default function ManageStaff() {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === "admin";

  const [staff, setStaff] = useState<User[]>([]);
  const [cabins, setCabins] = useState<Cabin[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("");
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [toggling, setToggling] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [userToDelete, setUserToDelete] = useState<{ id: number; name: string } | null>(null);
  const [assigningCabinUser, setAssigningCabinUser] = useState<User | null>(null);
  const [assignCabinGroupId, setAssignCabinGroupId] = useState<string>("");
  const [savingCabinAssign, setSavingCabinAssign] = useState(false);
  const [assignCabinGroups, setAssignCabinGroups] = useState<CabinGroup[]>([]);
  const [loadingAssignCabins, setLoadingAssignCabins] = useState(false);
  const [overrideCabinUser, setOverrideCabinUser] = useState<User | null>(null);
  const [sendingReset, setSendingReset] = useState(false);
  const [sendInviteToggle, setSendInviteToggle] = useState(false);
  // CampMinder role-assign modal
  const [roleModalUser, setRoleModalUser] = useState<User | null>(null);
  const [savingRole, setSavingRole] = useState(false);
  const [resendingInvite, setResendingInvite] = useState<number | null>(null);

  const fetchStaff = useCallback(() => {
    setLoading(true);
    api
      .get<User[]>("/admin/users")
      .then((res) => setStaff(res.data))
      .catch(() => toast.error("Failed to load staff"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchStaff(); }, [fetchStaff]);

  useEffect(() => {
    cabinsApi.list().then(setCabins).catch(() => toast.error("Failed to load cabins"));
  }, []);

  const form = useForm<CreateFormData | EditFormData>({
    resolver: zodResolver(editSchema),
  });

  const roleForm = useForm<RoleFormData>({
    resolver: zodResolver(roleSchema),
  });

  const openCreate = () => {
    setEditingUser(null);
    setSendInviteToggle(false);
    form.reset({ firstName: "", lastName: "", email: "", password: "", role: "counselor", cabinId: undefined });
    setModalOpen(true);
  };

  const openEdit = (user: User) => {
    setEditingUser(user);
    setSendInviteToggle(false);
    form.reset({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      password: "",
      role: user.role,
      cabinId: user.cabinId ?? undefined,
      excludedFromReports: user.excludedFromReports ?? false,
      notes: user.notes ?? "",
    });
    setModalOpen(true);
  };

  const openRoleModal = (user: User) => {
    setRoleModalUser(user);
    roleForm.reset({ role: user.role, password: "" });
  };

  const onSubmit = async (data: CreateFormData | EditFormData) => {
    // For create without invite, password is required
    if (!editingUser && !sendInviteToggle && (!data.password || data.password.length < 8)) {
      form.setError("password", { message: "Password must be at least 8 characters" });
      return;
    }

    setSaving(true);
    try {
      const payload: Record<string, unknown> = { ...data };
      // Don't send empty password on edit
      if (editingUser && !payload.password) {
        delete payload.password;
      }
      if (!editingUser && sendInviteToggle) {
        delete payload.password;
        payload.sendInvite = true;
      }

      if (editingUser) {
        await api.put(`/admin/users/${editingUser.id}`, payload);
        toast.success("Staff member updated!");
      } else {
        await api.post("/admin/users", payload);
        toast.success(
          sendInviteToggle
            ? "Staff member created! An invitation email has been sent."
            : "Staff member created!"
        );
      }
      setModalOpen(false);
      setEditingUser(null);
      fetchStaff();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : `Failed to ${editingUser ? "update" : "create"} staff member`);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async () => {
    if (!editingUser) return;
    setToggling(true);
    try {
      await api.patch(`/admin/users/${editingUser.id}/deactivate`);
      toast.success(editingUser.active ? "Staff member deactivated!" : "Staff member reactivated!");
      setModalOpen(false);
      setEditingUser(null);
      fetchStaff();
    } catch {
      toast.error("Failed to update staff member status");
    } finally {
      setToggling(false);
    }
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${userToDelete.id}`);
      toast.success("Staff member permanently deleted!");
      setDeleteConfirm(false);
      setUserToDelete(null);
      fetchStaff();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || "Failed to delete staff member");
    } finally {
      setDeleting(false);
    }
  };

  const handleSendReset = async (target?: User) => {
    const u = target ?? editingUser;
    if (!u) return;
    setSendingReset(true);
    try {
      await api.post(`/admin/users/${u.id}/send-reset`);
      toast.success(`Reset code sent to ${u.email}`);
    } catch {
      toast.error("Failed to send reset code");
    } finally {
      setSendingReset(false);
    }
  };

  const handleResendInvite = async (userId: number, email: string) => {
    setResendingInvite(userId);
    try {
      await api.post(`/admin/users/${userId}/resend-invite`);
      toast.success(`Invitation resent to ${email}`);
    } catch {
      toast.error("Failed to resend invitation");
    } finally {
      setResendingInvite(null);
    }
  };

  const handleSaveRole = async (data: RoleFormData) => {
    if (!roleModalUser) return;
    setSavingRole(true);
    try {
      const payload: { role: string; password?: string } = { role: data.role };
      if (data.password) payload.password = data.password;
      await api.patch(`/admin/users/${roleModalUser.id}/role`, payload);
      toast.success(data.password ? "Role and password updated!" : "Role updated!");
      setRoleModalUser(null);
      fetchStaff();
    } catch {
      toast.error("Failed to update role");
    } finally {
      setSavingRole(false);
    }
  };

  // Load cabin groups when the assign-cabin modal opens
  useEffect(() => {
    if (!assigningCabinUser) return;
    setLoadingAssignCabins(true);
    setAssignCabinGroupId("");
    cabinGroupsApi
      .list()
      .then((groups) => setAssignCabinGroups(groups.filter((g) => !g.archivedAt)))
      .catch(() => toast.error("Failed to load cabins"))
      .finally(() => setLoadingAssignCabins(false));
  }, [assigningCabinUser]);

  const handleSaveCabinAssign = async () => {
    if (!assigningCabinUser) return;
    setSavingCabinAssign(true);
    try {
      let cabinId: number | null = null;
      if (assignCabinGroupId) {
        const selectedGroup = assignCabinGroups.find((g) => g.id === Number(assignCabinGroupId));
        if (selectedGroup) {
          if (selectedGroup.cabins && selectedGroup.cabins.length > 0) {
            cabinId = selectedGroup.cabins[0].id;
          } else {
            const newCabin = await cabinsApi.create({ name: selectedGroup.name, cabinGroupId: selectedGroup.id });
            cabinId = newCabin.id;
          }
        }
      }
      await api.put(`/admin/users/${assigningCabinUser.id}`, {
        firstName: assigningCabinUser.firstName,
        lastName: assigningCabinUser.lastName,
        email: assigningCabinUser.email,
        role: assigningCabinUser.role,
        cabinId,
      });
      toast.success("Cabin assignment updated!");
      setAssigningCabinUser(null);
      setAssignCabinGroupId("");
      fetchStaff();
    } catch {
      toast.error("Failed to update cabin assignment");
    } finally {
      setSavingCabinAssign(false);
    }
  };

  const roleColor: Record<UserRole, "green" | "amber" | "bark" | "stone"> = {
    admin: "amber",
    director: "green",
    counselor: "bark",
    readonly: "stone",
  };

  const filteredStaff = staff.filter((u) => {
    if (!showInactive && u.active === false) return false;
    if (roleFilter && u.role !== roleFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      const fullName = `${u.firstName} ${u.lastName}`.toLowerCase();
      if (!fullName.includes(q) && !u.email.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const columns = [
    {
      key: "name",
      header: "Name",
      render: (row: User) => (
        <div className="flex items-center gap-3">
          <Avatar firstName={row.firstName} lastName={row.lastName} size="sm" />
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-stone-900">
              {row.firstName} {row.lastName}
            </span>
            {row.active === false && <Badge label="Inactive" color="stone" />}
            {row.active === false && row.campMinderPersonId == null && (
              <Badge label="Pending invite" color="amber" />
            )}
          </div>
        </div>
      ),
    },
    { key: "email", header: "Email" },
    {
      key: "role",
      header: "Role",
      render: (row: User) => (
        <Badge label={row.role} color={roleColor[row.role]} />
      ),
    },
    {
      key: "actions",
      header: "",
      render: (row: User) => (
        <div className="flex items-center gap-1">
          {/* Non-CampMinder users — direct cabin assignment */}
          {row.campMinderPersonId == null && (
            <button
              onClick={(e) => { e.stopPropagation(); setAssigningCabinUser(row); }}
              className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-forest-600 transition-colors"
              title="Assign cabin"
            >
              <HomeModernIcon className="w-4 h-4" />
            </button>
          )}
          {/* CampMinder counselors — override cabin per term */}
          {row.campMinderPersonId != null && row.role === "counselor" && (
            <button
              onClick={(e) => { e.stopPropagation(); setOverrideCabinUser(row); }}
              className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-forest-600 transition-colors"
              title="Override cabin"
            >
              <HomeModernIcon className="w-4 h-4" />
            </button>
          )}
          {row.campMinderPersonId == null && (
            <button
              onClick={(e) => { e.stopPropagation(); openEdit(row); }}
              className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-forest-600 transition-colors"
              title="Edit staff member"
            >
              <PencilSquareIcon className="w-4 h-4" />
            </button>
          )}
          {/* Resend invite for pending (inactive, non-CampMinder) users */}
          {row.campMinderPersonId == null && row.active === false && isAdmin && (
            <button
              onClick={(e) => { e.stopPropagation(); handleResendInvite(row.id, row.email); }}
              disabled={resendingInvite === row.id}
              className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-forest-600 transition-colors disabled:opacity-50"
              title="Resend invite email"
            >
              <EnvelopeIcon className="w-4 h-4" />
            </button>
          )}
          {/* CampMinder-synced users — role/cabin only */}
          {row.campMinderPersonId != null && isAdmin && (
            <button
              onClick={(e) => { e.stopPropagation(); openRoleModal(row); }}
              className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-forest-600 transition-colors"
              title="Set role & cabin"
            >
              <ShieldCheckIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Manage Staff"
        subtitle="Counselor and admin accounts"
        actions={
          <Button
            variant="primary"
            icon={<PlusIcon className="w-4 h-4" />}
            onClick={openCreate}
          >
            Add Staff
          </Button>
        }
      />

      <div className="p-6 lg:p-8">
        {/* Search + filter bar */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          {/* Name search */}
          <div className="relative flex-1 min-w-48">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email…"
              className="w-full pl-9 pr-8 py-2 text-sm border border-stone-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-forest-500 focus:border-transparent placeholder:text-stone-400"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Role filter */}
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="py-2 pl-3 pr-8 text-sm border border-stone-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-forest-500 focus:border-transparent text-stone-700"
          >
            <option value="">All roles</option>
            <option value="admin">Admin</option>
            <option value="director">Director</option>
            <option value="counselor">Counselor</option>
            <option value="readonly">Read Only</option>
          </select>

          {/* Show inactive toggle */}
          <label className="flex items-center gap-2 text-sm text-stone-500 cursor-pointer select-none whitespace-nowrap">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="rounded border-stone-300 text-forest-600 focus:ring-forest-500"
            />
            Show inactive / pending
          </label>

          {/* Active filter count */}
          {(search || roleFilter) && (
            <span className="text-xs text-stone-500">
              {filteredStaff.length} result{filteredStaff.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>

        <Table
          columns={columns}
          data={filteredStaff}
          keyExtractor={(row) => row.id}
          loading={loading}
          emptyMessage={
            <div className="text-center py-8">
              <UserGroupIcon className="w-10 h-10 text-stone-300 mx-auto mb-3" />
              <p className="text-stone-700 text-sm font-medium mb-1">No staff accounts found</p>
              <p className="text-stone-500 text-sm mb-4">
                Staff accounts are imported via CampMinder sync, or you can create them manually using the button above.
              </p>
              <Link
                to="/admin/config"
                className="inline-flex items-center gap-1.5 rounded-md border border-stone-300 px-3 py-1.5 text-sm font-medium text-stone-700 hover:bg-stone-50"
              >
                <ArrowPathIcon className="w-4 h-4" />
                Go to CampMinder Sync
              </Link>
            </div>
          }
        />
      </div>

      {/* Create / Edit modal */}
      <Modal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditingUser(null); }}
        title={editingUser ? "Edit Staff Member" : "Add Staff Member"}
      >
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input label="First Name" error={form.formState.errors.firstName?.message} {...form.register("firstName")} />
            <Input label="Last Name" error={form.formState.errors.lastName?.message} {...form.register("lastName")} />
          </div>
          <Input label="Email" type="email" error={form.formState.errors.email?.message} {...form.register("email")} />

          {/* Invite toggle (create only) */}
          {!editingUser && (
            <label className="flex items-center gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={sendInviteToggle}
                onChange={(e) => setSendInviteToggle(e.target.checked)}
                className="rounded border-stone-300 text-forest-600 focus:ring-forest-500"
              />
              <span className="text-sm text-stone-700">
                Send invitation email <span className="text-stone-400">(user sets their own password)</span>
              </span>
            </label>
          )}

          {/* Password field — hidden when invite toggle is on */}
          {!sendInviteToggle && (
            <Input
              label={editingUser ? "Password (leave blank to keep current)" : "Password"}
              type="password"
              error={form.formState.errors.password?.message}
              {...form.register("password")}
            />
          )}

          <Select
            label="Role"
            options={roleOptions}
            error={form.formState.errors.role?.message}
            {...form.register("role")}
          />
          <Select
            label="Cabin (optional)"
            options={[{ value: "", label: "None" }, ...cabins.map((c) => ({ value: c.id, label: c.cabinGroup ? `${c.cabinGroup.name} — ${c.name}` : c.name }))]}
            {...form.register("cabinId")}
          />
          {editingUser && (
            <label className="flex items-start gap-2 text-sm text-stone-700 cursor-pointer">
              <input
                type="checkbox"
                {...form.register("excludedFromReports")}
                className="mt-0.5 rounded border-stone-300 text-forest-600 focus:ring-forest-500"
              />
              <span>
                <span className="font-medium">Exclude from reports</span>
                <span className="block text-xs text-stone-500">
                  Hides this counselor from all generated and printed reports without deactivating the account.
                </span>
              </span>
            </label>
          )}
          {editingUser && (
            <div>
              <label htmlFor="staff-notes" className="block text-sm font-medium text-stone-700 mb-1.5">
                Notes (optional)
              </label>
              <textarea
                id="staff-notes"
                rows={4}
                {...form.register("notes")}
                className="block w-full rounded-lg border border-stone-300 text-sm px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-forest-500 focus:border-transparent"
                placeholder="Anything worth remembering about this staff member…"
              />
            </div>
          )}
          {/* Former-camper link (admin only, edit mode only) */}
          {editingUser && isAdmin && (
            <FormerCamperLink user={editingUser} />
          )}
          <div className="flex items-center justify-between gap-3 pt-2">
            {editingUser ? (
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  icon={<NoSymbolIcon className="w-4 h-4" />}
                  onClick={handleToggleActive}
                  loading={toggling}
                >
                  {editingUser.active === false ? "Reactivate" : "Deactivate"}
                </Button>
                {isAdmin && (
                  <Button
                    type="button"
                    variant="ghost"
                    icon={<EnvelopeIcon className="w-4 h-4" />}
                    onClick={() => handleSendReset()}
                    loading={sendingReset}
                    title="Send a 6-digit reset code to this user's email"
                  >
                    Send reset code
                  </Button>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  icon={<TrashIcon className="w-4 h-4" />}
                  onClick={() => {
                    setUserToDelete({ id: editingUser.id, name: `${editingUser.firstName} ${editingUser.lastName}` });
                    setModalOpen(false);
                    setEditingUser(null);
                    setDeleteConfirm(true);
                  }}
                >
                  Delete
                </Button>
              </div>
            ) : (
              <div />
            )}
            <div className="flex items-center gap-3">
              <Button type="button" variant="ghost" onClick={() => { setModalOpen(false); setEditingUser(null); }}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" loading={saving}>
                {editingUser ? "Save Changes" : (sendInviteToggle ? "Create & Send Invite" : "Create")}
              </Button>
            </div>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={deleteConfirm}
        onClose={() => { setDeleteConfirm(false); setUserToDelete(null); }}
        onConfirm={handleDelete}
        title="Delete Staff Member"
        message={`Are you sure you want to permanently delete "${userToDelete?.name}"? This action cannot be undone.`}
        confirmLabel="Delete Forever"
        variant="danger"
        loading={deleting}
      />

      {/* Assign cabin modal (non-CampMinder users) */}
      <Modal
        open={!!assigningCabinUser}
        onClose={() => { setAssigningCabinUser(null); setAssignCabinGroupId(""); }}
        title={`Assign Cabin — ${assigningCabinUser ? `${assigningCabinUser.firstName} ${assigningCabinUser.lastName}` : ""}`}
        size="sm"
      >
        <div className="space-y-4">
          <Select
            label="Cabin"
            placeholder={loadingAssignCabins ? "Loading cabins…" : "No cabin assigned"}
            options={assignCabinGroups.map((g) => ({ value: g.id, label: g.name }))}
            value={assignCabinGroupId}
            onChange={(e) => setAssignCabinGroupId(e.target.value)}
            disabled={loadingAssignCabins}
          />
          <div className="flex justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => { setAssigningCabinUser(null); setAssignCabinGroupId(""); }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleSaveCabinAssign}
              loading={savingCabinAssign}
            >
              Save
            </Button>
          </div>
        </div>
      </Modal>

      {/* Override cabin dialog (CampMinder counselors) — scoped to active term */}
      <OverrideCounselorCabinDialog
        user={overrideCabinUser}
        open={!!overrideCabinUser}
        onClose={() => setOverrideCabinUser(null)}
        onSuccess={fetchStaff}
      />

      {/* Set role / password modal (CampMinder-synced users) */}
      <Modal
        open={!!roleModalUser}
        onClose={() => setRoleModalUser(null)}
        title={`Set Role — ${roleModalUser ? `${roleModalUser.firstName} ${roleModalUser.lastName}` : ""}`}
        size="sm"
      >
        {roleModalUser && (
          <form onSubmit={roleForm.handleSubmit(handleSaveRole)} className="space-y-4">
            <p className="text-sm text-stone-500">
              This staff member is synced from CampMinder. You can set their role and password here.
            </p>
            <Select
              label="Role"
              options={roleOptions}
              error={roleForm.formState.errors.role?.message}
              {...roleForm.register("role")}
            />
            <Input
              label="Set password (leave blank to keep current)"
              type="password"
              autoComplete="new-password"
              placeholder="••••••••"
              error={roleForm.formState.errors.password?.message}
              {...roleForm.register("password")}
            />
            <div className="flex items-center justify-between gap-3 pt-2">
              <Button
                type="button"
                variant="ghost"
                icon={<EnvelopeIcon className="w-4 h-4" />}
                onClick={() => handleSendReset(roleModalUser)}
                loading={sendingReset}
                title="Send a 6-digit reset code to this user's email so they can set a password"
              >
                Send reset code
              </Button>
              <div className="flex items-center gap-3">
                <Button type="button" variant="ghost" onClick={() => setRoleModalUser(null)}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" loading={savingRole}>
                  Save
                </Button>
              </div>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
